import * as React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import { Button } from "@progress/kendo-react-buttons";
import { Breadcrumb } from "@progress/kendo-react-layout";
import { useQueryClient } from "react-query";
import { QUERY_KEYS } from "../../data/helpers/queryKeys";

import {
  useQueryGetClient,
  useQueryGetModelMaxContributions,
  useQueryGetModelDetail,
  useMutateUpdateModelRates,
  useMutatePublishModel,
  useMutateUnlockModel,
  useMutateUpdateModelName,
  useQueryGetDisclosureStatus,
} from "../../data/services/clientService";

import ClientRateModelSummary from "./ClientRateModelSummary";
import ClientRateModelGrid from "./ClientRateModelGrid";
import { setRateGridFields, getBenefitTypeOrder } from "./ClientRateModelHelper";

import LoadingIndicator from "../../components/LoadingIndicator";
import ErrorBanner from "../../components/ErrorBanner";
import SuccessBanner from "../../components/SuccessBanner";
import AlertDialog from "../../components/AlertDialog";
import ClientRateModelAddPlan from "./ClientRateModelAddPlan";
import ClientRateModelErrorDialog from "./ClientRateModelErrorDialog";
import ClientMaxContributionModel from "./ClientMaxContributionModel";
import DisclosureModal from "./DisclosureModal";
import OverrideOEStatusDialog from "./OverrideOEStatusDialog";
import ThankYouModal from "./ThankYouModal";
import ClientCopyContributionDialog from "./ClientCopyContributionDialog";
import UserContext from "../../layouts/UserContext";
import OEContext from "../../layouts/OEContext";
import MultipleMedicalCarriersDialog from "../../components/MultipleMedicalCarriersDialog";
import {
  useQueryGetClientRateModelSubmitCarrierRule,
  isBlockedBySubmitCarrierRule,
} from "../../data/services/featureAccessService";
import { useQueryGetOEPeriodList } from "../../data/services/openEnrollmentService";
import { axiosDownload } from "../../data/helpers/baseApi";
import { SERVICE_URLS } from "../../data/helpers/serviceUrls";
import EmployeeCensusReport from "../../pages/userdashboards/EmployeeCensusReport";
import EmployerHSAForm from "../../pages/client/ClientHSAContribution";
import EmployerDCAForm from "../../pages/client/ClientDCAConfiguration";
import EmployerFSAForm from "../../pages/client/ClientFSAContribution";
import ManageSubClientsDialog from "./ManageSubClientsDialog";
import ParentSubmitWarningDialog from "./ParentSubmitWarningDialog";

const tabContentSpacingStyle = {
  paddingLeft: "100px",
  boxSizing: "border-box",
};

// Clones each group with a stable, benefit-type-sorted rates array. Two
// reasons this clone is important:
//  1. Kendo's groupBy re-orders group headers alphabetically; the grid
//     supplies a custom compare (see buildPlanNameGroupDescriptor) to render
//     Medical → Dental → Vision, but pre-sorting keeps any consumer that
//     reads `rates` directly consistent with the visible order.
//  2. onRateDataChange mutates `group.rates = rates` on the original model
//     object. If we passed the raw group down, `data.rates` in the grid
//     would flip references on every edit, which invalidates the grid's
//     `planNameGroup` memo and produces a render loop. Handing the grid a
//     clone decouples its `data.rates` from that mutation.
const sortGroupRatesForDisplay = (group) => {
  if (!group) {
    return group;
  }

  const sortedRates = [...(group?.rates ?? [])].sort((left, right) => {
    const leftOrder = getBenefitTypeOrder(left?.planOfferType);
    const rightOrder = getBenefitTypeOrder(right?.planOfferType);

    if (leftOrder !== rightOrder) {
      return leftOrder - rightOrder;
    }

    return String(left?.planName ?? "").localeCompare(String(right?.planName ?? ""));
  });

  return {
    ...group,
    rates: sortedRates,
  };
};

const ClientRateModelLayout = () => {
  const { clientId, oePeriodId, versionId } = useParams();

  // Carrier submission verdict. The server owns the rule - which of aetna,
  // aetna-uhc or aetna-oxford applies, and which carrier each permits - so this
  // screen only has to render the answer. Fails safe to "allowed" while loading
  // and on error, matching the gate it replaces.
  const submitCarrierRuleQuery = useQueryGetClientRateModelSubmitCarrierRule(
    oePeriodId,
    clientId,
    versionId,
  );
  const submitCarrierRule = submitCarrierRuleQuery.data?.data;
  const isCarrierSubmitBlocked = isBlockedBySubmitCarrierRule(
    submitCarrierRuleQuery,
  );
  const [
    showMultipleMedicalCarriersModal,
    setShowMultipleMedicalCarriersModal,
  ] = React.useState(false);
  const [showOverrideStatusDialog, setShowOverrideStatusDialog] =
    React.useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { loggedInUser } = React.useContext(UserContext);
  const oePeriod = React.useContext(OEContext);
  const { data: oePeriodListResponse } = useQueryGetOEPeriodList();
  const urlMatchedOEPeriod = React.useMemo(() => {
    const list = oePeriodListResponse?.data;
    if (!Array.isArray(list)) {
      return null;
    }
    return (
      list.find((entry) => `${entry?.oePeriodId}` === `${oePeriodId}`) ?? null
    );
  }, [oePeriodListResponse, oePeriodId]);
  const effectiveOEPeriod = urlMatchedOEPeriod ?? oePeriod;
  const queryClient = useQueryClient();

  const [client, setClient] = React.useState({});
  const [selected, setSelected] = React.useState(0);
  const [newModelDetailData, setNewModelDetailData] = React.useState();
  const [isSavingDraft, setIsSavingDraft] = React.useState(false);
  const [showAddPlanModel, setShowAddPlanModel] = React.useState(false);

  const [showCopyContribModel, setShowCopyContribModel] = React.useState(false);
  const [showPublishErrorModel, setShowPublishErrorModel] =
    React.useState(false);
  const [currentGroupId, setCurrentGroupId] = React.useState("");
  const [lastRefreshTime, setLastRefreshTime] = React.useState(null);

  const [showMaxContributionModel, setShowMaxContributionModel] =
    React.useState(false);
  const [currentGroupMaxContrib, setCurrentGroupMaxContrib] =
    React.useState(null);
  const [maxContributionData, setMaxContributionData] = React.useState([]);
  const [showThankYou, setShowThankYou] = React.useState(false);
  const [showSubmitErrorModel, setShowSubmitErrorModel] = React.useState(false);

  const [showSubmitConfirmModel, setShowSubmitConfirmModel] =
    React.useState(false);

  const [showSubClientsModal, setShowSubClientsModal] = React.useState(false);
  const [showParentSubmitWarning, setShowParentSubmitWarning] =
    React.useState(false);

  // Hotfix: force-disabled until SOF-256 apply-to-children behavior is finalized.
  const isParentModelUiEnabled = false;

  const { isLoading: clientLoading, data: clientResponse } = useQueryGetClient(
    oePeriodId,
    clientId,
  );
  const { isLoading: newModelDetailLoading, data: newModelDetail } =
    useQueryGetModelDetail(oePeriodId, clientId, versionId);
  const { isLoading: maxContribLoading, data: maxContrib } =
    useQueryGetModelMaxContributions(oePeriodId, clientId, versionId);

  const { data: disclosureStatusData } = useQueryGetDisclosureStatus(
    oePeriodId,
    clientId,
  );
  const disclosureStatus = disclosureStatusData?.data ?? null;
  const hasDisclosure = disclosureStatus?.hasDisclosure ?? false;
  const isDisclosureDeadlinePassed =
    disclosureStatus?.isDeadlinePassed ?? false;
  const disclosureDeadline = disclosureStatus?.disclosureDeadline
    ? new Date(disclosureStatus.disclosureDeadline).toLocaleDateString(
        "en-US",
        { year: "numeric", month: "long", day: "numeric" },
      )
    : null;

  const isBuiltByBroker = newModelDetailData?.isCreatedByBroker ?? false;

  const isBrokerUser = loggedInUser?.brokerUserPrivilege ?? false;
  const showCensusReportTab = !isBrokerUser;

  const useMutateUpdateModelRatesHandler = useMutateUpdateModelRates();
  const useMutatePublishModelHandler = useMutatePublishModel();
  const useMutateUnlockModelHandler = useMutateUnlockModel();

  const isRateSubmitted =
    client.oeStatus === "Submitted" ||
    client.oeStatus === "PublishInProgress" ||
    client.oeStatus === "PublishError" ||
    client.oeStatus === "Published" ||
    client.oeStatus === "PublishedDefault";
  const isClientRateSubmitted = client.oeStatus === "Submitted";
  const isClientRatePublishError = client.oeStatus === "PublishError";
  const isClientRatePublishSuccess = client.oeStatus === "Published";
  const isClientRatePublishDefaultSuccess =
    client.oeStatus === "PublishedDefault";

  const ownersGroup = React.useMemo(() => {
    const groups = newModelDetailData?.groups ?? [];
    return (
      groups.find((group) => {
        const normalizedName = (group?.groupName ?? "").trim().toLowerCase();
        return normalizedName === "owners" || normalizedName === "owner";
      }) ?? null
    );
  }, [newModelDetailData?.groups]);

  const nonOwnerGroups = React.useMemo(() => {
    const groups = newModelDetailData?.groups ?? [];
    return groups.filter((group) => {
      const isOwners = ownersGroup && group?.groupId === ownersGroup.groupId;

      return !isOwners;
    });
  }, [newModelDetailData?.groups, ownersGroup]);

  // Memoize the cloned/sorted group objects so their `rates` array references
  // stay stable across parent re-renders. Recomputing on every render would
  // produce a new rates reference each time, invalidating the grid's
  // planNameGroup memo and firing its data-reset effect in a loop.
  const displayOwnersGroup = React.useMemo(
    () => sortGroupRatesForDisplay(ownersGroup),
    [ownersGroup],
  );

  const displayGroups = React.useMemo(
    () => (nonOwnerGroups ?? []).map(sortGroupRatesForDisplay),
    [nonOwnerGroups],
  );

  const hasHdMedicalPlanSelected = React.useMemo(() => {
    const groups = newModelDetailData?.groups ?? [];
    const hdPlanPattern = /\bHDHP\b|HIGH\s*DEDUCTIBLE|\bHSA\b/i;

    return groups.some((group) =>
      (group?.rates ?? []).some((rate) => {
        const isMedicalOfferType =
          String(rate?.planOfferType ?? "").toUpperCase() === "MED";

        if (!isMedicalOfferType) {
          return false;
        }

        const searchableText = [
          rate?.planName,
          rate?.planId,
          rate?.planTypeDescription,
          rate?.groupName,
        ]
          .filter(Boolean)
          .join(" ");

        return hdPlanPattern.test(searchableText);
      }),
    );
  }, [newModelDetailData?.groups]);

  // Distinct, non-terminated medical plans in the current model. HSA can only
  // contribute to high-deductible plans; FSA and DCA can use any medical plan.
  const nonTerminatedMedicalPlans = React.useMemo(() => {
    const groups = newModelDetailData?.groups ?? [];
    const seen = new Set();
    const plans = [];

    groups.forEach((group) => {
      (group?.rates ?? []).forEach((rate) => {
        const isMedical =
          String(rate?.planOfferType ?? "").toUpperCase() === "MED";
        const planId = rate?.planId;

        if (!isMedical || rate?.isTermed || !planId || seen.has(planId)) {
          return;
        }

        seen.add(planId);
        plans.push({
          value: planId,
          label: rate?.planName ?? planId,
          isHighDeductible: Boolean(rate?.isHighDeductible),
        });
      });
    });

    return plans;
  }, [newModelDetailData?.groups]);

  const hsaPlanOptions = React.useMemo(
    () =>
      nonTerminatedMedicalPlans
        .filter((plan) => {
          return plan.isHighDeductible;
        })
        .map(({ value, label }) => ({ value, label })),
    [nonTerminatedMedicalPlans],
  );

  const hsaBenefitGroupOptions = React.useMemo(() => {
    return (newModelDetailData?.groups ?? [])
      .filter((group) => {
        return (group?.rates ?? []).some((rate) => rate.isHighDeductible);
      })
      .map((group) => ({
        value: group.groupId,
        label: group.groupName,
      }));
  }, [newModelDetailData?.groups]);

  const medicalPlanOptions = React.useMemo(
    () =>
      nonTerminatedMedicalPlans.map(({ value, label }) => ({ value, label })),
    [nonTerminatedMedicalPlans],
  );

  const medicalBenefitGroupOptions = React.useMemo(
    () =>
      (newModelDetailData?.groups ?? []).map((group) => ({
        value: group.groupId,
        label: group.groupName,
      })),
    [newModelDetailData?.groups],
  );

  // Tab indices must be computed dynamically because the Owners tab is
  // conditional and the number of nonOwnerGroup tabs is variable. The order
  // matches the JSX render order in RenderTab:
  //   Summary, [Owners], CensusReport, ...nonOwnerGroups, HSA, DCA, FSA
  const tabIndex = React.useMemo(() => {
    let idx = 0;
    const indices = {};
    indices.Summary = idx++;
    if (ownersGroup) indices.Owners = idx++;
    if (showCensusReportTab) {
      indices.CensusReport = idx++;
    }
    (nonOwnerGroups ?? []).forEach((group) => {
      const normalizedName = (group?.groupName ?? "").trim().toLowerCase();
      if (
        normalizedName === "all employees" ||
        normalizedName === "all employee"
      ) {
        indices.AllEmployees = idx;
      }
      idx++;
    });
    indices.HSA = idx++;
    indices.DCA = idx++;
    indices.FSA = idx++;
    return indices;
  }, [ownersGroup, nonOwnerGroups, showCensusReportTab]);

  const [isEditingModelName, setIsEditingModelName] = React.useState(false);
  const [editedModelName, setEditedModelName] = React.useState("");
  const updateModelNameMutation = useMutateUpdateModelName();

  const handleDisclosureAccept = () => {
    setShowSubmitConfirmModel(false);
    setShowThankYou(true);
  };

  const handleThankYouClose = () => {
    setShowThankYou(false);
  };

  React.useEffect(() => {
    if (!newModelDetailLoading && newModelDetail && newModelDetail.data) {
      setRateGridFields(newModelDetail);
      setNewModelDetailData(newModelDetail.data);
    }
  }, [newModelDetailLoading, newModelDetail]);

  React.useEffect(() => {
    if (!maxContribLoading && maxContrib && maxContrib.data) {
      setMaxContributionData(maxContrib.data ?? []);
    }
  }, [maxContribLoading, maxContrib]);

  React.useEffect(() => {
    if (!clientLoading && clientResponse && clientResponse.data) {
      setClient(clientResponse.data);
    } else if (!clientLoading && clientResponse && !clientResponse.data) {
      if (loggedInUser?.hasAdminAppPrivilege) {
        navigate("/admin/clientadminsummary");
      } else {
        navigate("/dashboard");
      }
    }
  }, [clientLoading, clientResponse, navigate, loggedInUser]);

  // This screen is a live rate-model editor (add/terminate plans, set max
  // contribution, contribution shortcuts, save draft, submit, edit model
  // name) with no per-control gate. Sales Admins are read-only, so rather
  // than gate each control, send them back to the read-only Dashboard.
  React.useEffect(() => {
    if (loggedInUser?.salesAdminPrivilege) {
      navigate(`/clients/${clientId}/oe/${oePeriodId}/summary`, {
        replace: true,
      });
    }
  }, [loggedInUser, clientId, oePeriodId, navigate]);

  React.useEffect(() => {
    const requestedTab = new URLSearchParams(location.search).get("tab");

    if (requestedTab && tabIndex[requestedTab] !== undefined) {
      setSelected(tabIndex[requestedTab]);
    }
  }, [location.search, tabIndex]);

  if (
    newModelDetailLoading ||
    clientLoading ||
    loggedInUser?.salesAdminPrivilege
  ) {
    return <LoadingIndicator />;
  }

  if (!client) {
    return <div>Client not found</div>;
  }

  const handleSaveModelName = async () => {
    setIsEditingModelName(false);

    if (
      editedModelName &&
      editedModelName.trim() !== newModelDetailData?.modelName?.trim()
    ) {
      try {
        await updateModelNameMutation.mutateAsync({
          modelVersionId: newModelDetailData?.modelVersionId,
          modelName: editedModelName,
        });

        setNewModelDetailData((prev) => ({
          ...prev,
          modelName: editedModelName,
        }));
      } catch (err) {}
    }
  };

  const RenderNav = () => {
    let data = [
      {
        id: "home",
        text: loggedInUser?.hasAdminAppPrivilege
          ? `${client?.clientId} - ${client?.clientName}`
          : "Home",
        iconClass: "k-i-home",
      },
      {
        id: "Model",
        text: newModelDetailData?.modelName ?? "",
      },
    ];

    const handleItemSelect = (event) => {
      if (event.id === "home") {
        if (loggedInUser?.hasAdminAppPrivilege) {
          navigate(`/admin/clients/${clientId}/oe/${oePeriodId}/summary`);
        } else {
          navigate(`/clients/${clientId}/oe/${oePeriodId}/summary`);
        }
      }
    };

    return (
      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <Breadcrumb
          data={data}
          className={"oe-breadcrumb"}
          onItemSelect={handleItemSelect}
        />

        {isEditingModelName ? (
          <input
            type="text"
            value={editedModelName}
            onChange={(e) => setEditedModelName(e.target.value)}
            onBlur={handleSaveModelName}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSaveModelName();
            }}
            autoFocus
            style={{
              fontSize: "14px",
              padding: "4px",
              borderRadius: "4px",
              border: "1px solid #ccc",
              width: "250px",
            }}
          />
        ) : (
          <Button
            look="bare"
            icon="edit"
            title="Edit Model Name"
            onClick={() => {
              setEditedModelName(newModelDetailData?.modelName || "");
              setIsEditingModelName(true);
            }}
          />
        )}
      </div>
    );
  };

  const RenderTab = () => {
    const handleSelect = (e) => {
      setSelected(e.selected);
    };

    const formatTitle = (title) => {
      if (title && title.length > 20) {
        return title.substring(0, 20) + "...";
      } else {
        return title;
      }
    };

    const onRateDataChange = (rates, groupId) => {
      const group = newModelDetailData.groups.find(
        (g) => g.groupId === groupId,
      );
      if (group) {
        group.rates = rates;
        setNewModelDetailData(newModelDetailData);
      }
    };

    const onSubmitClick = () => {
      // Carrier feature keys can forbid this combination of medical carriers.
      // Checked before the missing-data guard so the carrier rule takes
      // precedence, and only when the server actually says no.
      if (isCarrierSubmitBlocked) {
        setShowMultipleMedicalCarriersModal(true);
        return;
      }

      // SOF-838: carrier-terminated plans carry no contribution by design, so they must not
      // count as missing data - otherwise they block submission permanently.
      let hasMissingData = newModelDetailData.groups.some((group) =>
        group.rates.some(
          (e) =>
            !e.isCarrierTerminated && !e.percentSelected && !e.fixedValueSelected,
        ),
      );

      if (hasMissingData) {
        setShowSubmitErrorModel(true);
        return;
      }

      if (
        isParentModelUiEnabled &&
        newModelDetailData?.isParentModel &&
        newModelDetailData?.subClients?.length > 0
      ) {
        setShowParentSubmitWarning(true);
      } else {
        setShowSubmitConfirmModel(true);
      }
    };

    const onPublishClick = async () => {
      let inputData = {
        oePeriodId: oePeriodId,
        clientId: clientId,
        versionId: versionId,
        isRetry: false,
      };

      await useMutatePublishModelHandler.mutateAsync(inputData);
    };

    const onRetryPublishClick = async () => {
      let inputData = {
        oePeriodId: oePeriodId,
        clientId: clientId,
        versionId: versionId,
        isRetry: true,
      };

      await useMutatePublishModelHandler.mutateAsync(inputData);
    };

    const onUnlockClick = async () => {
      let inputData = {
        oePeriodId: oePeriodId,
        clientId: clientId,
        versionId: versionId,
      };

      await useMutateUnlockModelHandler.mutateAsync(inputData);
    };

    const onSaveDraft = async () => {
      setIsSavingDraft(true);

      let ratesToUpdate = newModelDetailData.groups.flatMap((group) => {
        return group.rates
          .filter((e) => e.percentSelected || e.fixedValueSelected)
          .map((rate) => {
            return {
              clientRateModelId: rate.clientRateModelId,
              method: rate.percentSelected ? "P" : "F",
              contributionValue: rate.percentSelected
                ? (rate.percentValue ? rate.percentValue : 0) * 100
                : rate.fixedValue
                  ? rate.fixedValue
                  : 0,
              employeeContributionValue: rate.newEmployeeContribution ?? 0,
            };
          });
      });

      if (ratesToUpdate.length > 0) {
        let data = {
          clientId: clientId,
          oePeriodId: oePeriodId,
          versionId: versionId,
          rates: ratesToUpdate,
        };

        await useMutateUpdateModelRatesHandler.mutateAsync(data);
      }

      setIsSavingDraft(false);
    };

    const onManagePlanClick = (groupId) => {
      setCurrentGroupId(groupId);
      setShowAddPlanModel(true);
    };

    const onCopyContributionClick = (groupId) => {
      setCurrentGroupId(groupId);
      setShowCopyContribModel(true);
    };

    const handleSetMaxContribution = (groupMaxContrib, groupId) => {
      if (!groupMaxContrib) {
        groupMaxContrib = {
          groupId: groupId,
        };
      }

      setCurrentGroupMaxContrib(groupMaxContrib);
      setShowMaxContributionModel(true);
    };

    const onExcelExportClick = async () => {
      await axiosDownload(
        SERVICE_URLS.CLIENT_MODEL_EXCEL_DOWNLOAD(
          oePeriodId,
          clientId,
          versionId,
          client.clientName,
        ),
        `${clientId} - ${newModelDetailData?.modelName} - Contribution Model.xlsx`,
      );
    };

    return (
      <OEContext.Provider value={effectiveOEPeriod}>
        <div
          style={{
            width: "100%",
            minHeight: "calc(100vh - 180px)",
            boxSizing: "border-box",
          }}
        >
          <TabStrip
            selected={selected}
            onSelect={handleSelect}
            dir={""}
            tabPosition={"left"}
            style={{ width: "100%" }}
          >
            <TabStripTab title={"Summary"} key={"Summary"}>
              <div className="ratesummary" style={tabContentSpacingStyle}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "20px",
                  }}
                >
                  <h2 style={{ marginTop: 0, marginBottom: 0 }}>{"Summary"}</h2>
                  <div>
                    <Button themeColor={"success"} onClick={onExcelExportClick}>
                      Export
                    </Button>
                  </div>
                </div>
                {ClientRateModelSummary(newModelDetailData)}
                <NewOfferingPlanDetailsMessage modelData={newModelDetailData} />

                {isBuiltByBroker && !isRateSubmitted && (
                  <div
                    style={{
                      background: "#fff3cd",
                      border: "1px solid #ffc107",
                      borderRadius: "4px",
                      padding: "10px 14px",
                      margin: "16px 0",
                      fontSize: "13px",
                      color: "#856404",
                    }}
                  >
                    <strong>
                      &#9888; This model was built on your behalf by your
                      broker.
                    </strong>{" "}
                    Please review it before submitting.
                  </div>
                )}

                {isDisclosureDeadlinePassed &&
                  !hasDisclosure &&
                  !isRateSubmitted && (
                    <div
                      style={{
                        background: "#f8d7da",
                        border: "1px solid #f5c2c7",
                        borderRadius: "4px",
                        padding: "10px 14px",
                        margin: "16px 0",
                        fontSize: "13px",
                        color: "#842029",
                      }}
                    >
                      <strong>&#9888; Disclosure deadline has passed.</strong>{" "}
                      The required acknowledgment was due by{" "}
                      {disclosureDeadline}. Please contact Operations to
                      proceed.
                    </div>
                  )}
                {!isBrokerUser &&
                  !isRateSubmitted &&
                  client.oeStatus !== "NotStarted" && (
                    <div style={{ float: "right", marginTop: "30px" }}>
                      <Button
                        themeColor={"success"}
                        onClick={onSubmitClick}
                        disabled={isSavingDraft || isDisclosureDeadlinePassed}
                        title={
                          isDisclosureDeadlinePassed
                            ? "Disclosure deadline has passed. Contact Operations."
                            : undefined
                        }
                      >
                        {" "}
                        Submit Contribution Model{" "}
                      </Button>
                    </div>
                  )}
                {client.oeStatus === "NotStarted" && loggedInUser?.isAdmin && (
                  <div style={{ float: "right", marginTop: "30px", textAlign: "right" }}>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#664d03",
                        marginBottom: "6px",
                      }}
                    >
                      A model cannot be submitted until the client takes
                      action in this portal. Use Override Status if
                      they&apos;re unable to log into the portal.
                    </div>
                    <Button
                      themeColor={"warning"}
                      onClick={() => setShowOverrideStatusDialog(true)}
                      title="Override this client's Not Started status so a contribution model can be submitted on their behalf. Requires a reason."
                    >
                      Override Status
                    </Button>
                  </div>
                )}
                {isClientRateSubmitted &&
                  loggedInUser?.hasAdminAppPrivilege && (
                    <div style={{ float: "right", marginTop: "30px" }}>
                      <Button
                        themeColor={"secondary"}
                        onClick={onUnlockClick}
                        style={{ marginRight: "20px" }}
                      >
                        {" "}
                        Unlock Grid
                      </Button>
                      <Button themeColor={"success"} onClick={onPublishClick}>
                        {" "}
                        Publish Rates To PrismHR
                      </Button>
                    </div>
                  )}
                {isClientRatePublishError &&
                  loggedInUser?.hasAdminAppPrivilege && (
                    <div style={{ float: "right", marginTop: "30px" }}>
                      <Button
                        themeColor={"secondary"}
                        onClick={onUnlockClick}
                        style={{ marginRight: "20px" }}
                      >
                        {" "}
                        Unlock Grid
                      </Button>
                      <Button
                        themeColor={"primary"}
                        onClick={onRetryPublishClick}
                      >
                        {" "}
                        Retry Publish Rates To PrismHR
                      </Button>
                    </div>
                  )}
                {isClientRatePublishSuccess && loggedInUser?.hasUnlockPriv && (
                  <div style={{ float: "right", marginTop: "30px" }}>
                    <Button
                      themeColor={"secondary"}
                      onClick={onUnlockClick}
                      style={{ marginRight: "20px" }}
                    >
                      {" "}
                      Unlock Grid
                    </Button>
                  </div>
                )}
                {isClientRatePublishDefaultSuccess &&
                  loggedInUser.hasUnlockPriv && (
                    <div style={{ float: "right", marginTop: "30px" }}>
                      <Button
                        themeColor={"secondary"}
                        onClick={onUnlockClick}
                        style={{ marginRight: "20px" }}
                      >
                        {" "}
                        Unlock Grid
                      </Button>
                    </div>
                  )}
              </div>
            </TabStripTab>

            {ownersGroup && (
              <TabStripTab
                title={formatTitle(ownersGroup.groupName)}
                key={ownersGroup.groupId}
              >
                <div className="ratemodel" style={tabContentSpacingStyle}>
                  <div
                    style={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <h2 style={{ marginTop: 0 }}>
                      Class: {ownersGroup.groupId} - {ownersGroup.groupName}
                    </h2>
                    {!isRateSubmitted && (
                      <div>
                        <Button
                          themeColor={"info"}
                          onClick={() => onManagePlanClick(ownersGroup.groupId)}
                          style={{ marginRight: "20px" }}
                        >
                          {" "}
                          Add or Terminate Plans
                        </Button>
                        <Button
                          themeColor="info"
                          onClick={() =>
                            handleSetMaxContribution(
                              maxContributionData.find(
                                (e) => e.groupId === ownersGroup.groupId,
                              ),
                              ownersGroup.groupId,
                            )
                          }
                          style={{ marginRight: "20px" }}
                        >
                          Set Max Medical Plan Contribution
                        </Button>
                        <Button
                          themeColor="info"
                          onClick={() =>
                            onCopyContributionClick(ownersGroup.groupId)
                          }
                          style={{ marginRight: "20px" }}
                        >
                          {"Contribution Shortcuts"}
                        </Button>
                        <Button
                          themeColor={"success"}
                          onClick={onSaveDraft}
                          disabled={isSavingDraft}
                        >
                          {" "}
                          Save Draft{" "}
                        </Button>
                      </div>
                    )}
                  </div>
                  <ClientRateModelGrid
                    data={displayOwnersGroup}
                    maxContribution={maxContributionData.find(
                      (e) => e.groupId === ownersGroup.groupId,
                    )}
                    dataChange={onRateDataChange}
                    isRateSubmitted={isRateSubmitted}
                    newOfferingOfferTypes={
                      newModelDetailData?.newOfferingOfferTypes ?? []
                    }
                    hasDentalNewOffering={
                      newModelDetailData?.hasDentalNewOffering
                    }
                    hasVisionNewOffering={
                      newModelDetailData?.hasVisionNewOffering
                    }
                    orderPlanGroupsByBenefitType={true}
                  ></ClientRateModelGrid>
                  {!isRateSubmitted && (
                    <div
                      style={{
                        float: "right",
                        marginTop: "30px",
                        paddingBottom: "30px",
                      }}
                    >
                      <Button
                        themeColor={"success"}
                        onClick={onSaveDraft}
                        disabled={isSavingDraft}
                      >
                        {" "}
                        Save Draft{" "}
                      </Button>
                    </div>
                  )}
                </div>
              </TabStripTab>
            )}

            {showCensusReportTab && (
              <TabStripTab title={"Census Report"} key={"CensusReport"}>
                <div className="ratesummary" style={tabContentSpacingStyle}>
                  <EmployeeCensusReport clientId={clientId} />
                </div>
              </TabStripTab>
            )}

            {newModelDetailData &&
              displayGroups &&
              displayGroups.map((displayGroup, index) => {
                const groupMaxContrib = maxContributionData.find(
                  (e) => e.groupId === displayGroup.groupId,
                );
                const hasGroupMaxContrib =
                  groupMaxContrib &&
                  (groupMaxContrib.eeContrib > 0 ||
                    groupMaxContrib.ecContrib > 0 ||
                    groupMaxContrib.esContrib > 0 ||
                    groupMaxContrib.famContrib > 0);

                return (
                  <TabStripTab title={formatTitle(displayGroup.groupName)} key={index}>
                    <div className="ratemodel" style={tabContentSpacingStyle}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <h2 style={{ marginTop: 0 }}>
                          Class: {displayGroup.groupId} - {displayGroup.groupName}
                        </h2>
                        {!isRateSubmitted && (
                          <div>
                            <Button
                              themeColor={"info"}
                              onClick={() => onManagePlanClick(displayGroup.groupId)}
                              style={{ marginRight: "20px" }}
                            >
                              {" "}
                              Add or Terminate Plans
                            </Button>
                            <Button
                              themeColor="info"
                              onClick={() =>
                                handleSetMaxContribution(
                                  groupMaxContrib,
                                  displayGroup.groupId,
                                )
                              }
                              style={{ marginRight: "20px" }}
                            >
                              {hasGroupMaxContrib > 0
                                ? "Set Max Medical Plan Contribution"
                                : "Set Max Medical Plan Contribution"}
                            </Button>
                            <Button
                              themeColor="info"
                              onClick={() =>
                                onCopyContributionClick(displayGroup.groupId)
                              }
                              style={{ marginRight: "20px" }}
                            >
                              {"Contribution Shortcuts"}
                            </Button>
                            <Button
                              themeColor={"success"}
                              onClick={onSaveDraft}
                              disabled={isSavingDraft}
                            >
                              {" "}
                              Save Draft{" "}
                            </Button>
                          </div>
                        )}
                      </div>
                      <ClientRateModelGrid
                        data={displayGroup}
                        maxContribution={groupMaxContrib}
                        dataChange={onRateDataChange}
                        isRateSubmitted={isRateSubmitted}
                        newOfferingOfferTypes={
                          newModelDetailData?.newOfferingOfferTypes ?? []
                        }
                        hasDentalNewOffering={
                          newModelDetailData?.hasDentalNewOffering
                        }
                        hasVisionNewOffering={
                          newModelDetailData?.hasVisionNewOffering
                        }
                        orderPlanGroupsByBenefitType={true}
                      ></ClientRateModelGrid>
                      {!isRateSubmitted && (
                        <div
                          style={{
                            float: "right",
                            marginTop: "30px",
                            paddingBottom: "30px",
                          }}
                        >
                          <Button
                            themeColor={"success"}
                            onClick={onSaveDraft}
                            disabled={isSavingDraft}
                          >
                            {" "}
                            Save Draft{" "}
                          </Button>
                        </div>
                      )}
                    </div>
                  </TabStripTab>
                );
              })}

            <TabStripTab title={"HSA"} key={"HSA"}>
              <div
                style={{
                  ...tabContentSpacingStyle,
                  width: "100%",
                  minHeight: "calc(100vh - 260px)",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  boxSizing: "border-box",
                }}
              >
                <EmployerHSAForm
                  clientId={clientId}
                  oePeriodId={oePeriodId}
                  versionId={versionId}
                  oePeriod={oePeriod}
                  hasHdMedicalPlanSelected={hasHdMedicalPlanSelected}
                  planOptions={hsaPlanOptions}
                  benefitGroupOptions={hsaBenefitGroupOptions}
                />
              </div>
            </TabStripTab>

            <TabStripTab title={"DCA"} key={"DCA"}>
              <div
                style={{
                  ...tabContentSpacingStyle,
                  width: "100%",
                  minHeight: "calc(100vh - 260px)",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  boxSizing: "border-box",
                }}
              >
                <EmployerDCAForm
                  clientId={clientId}
                  oePeriodId={oePeriodId}
                  versionId={versionId}
                  oePeriod={oePeriod}
                  planOptions={medicalPlanOptions}
                />
              </div>
            </TabStripTab>

            <TabStripTab title={"FSA"} key={"FSA"}>
              <div
                style={{
                  ...tabContentSpacingStyle,
                  width: "100%",
                  minHeight: "calc(100vh - 260px)",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  boxSizing: "border-box",
                }}
              >
                <EmployerFSAForm
                  clientId={clientId}
                  oePeriodId={oePeriodId}
                  versionId={versionId}
                  oePeriod={oePeriod}
                  planOptions={medicalPlanOptions}
                  benefitGroupOptions={medicalBenefitGroupOptions}
                />
              </div>
            </TabStripTab>
          </TabStrip>
        </div>
        {showMultipleMedicalCarriersModal && (
          <MultipleMedicalCarriersDialog
            carrierNames={submitCarrierRule?.carrierNames}
            message={submitCarrierRule?.message}
            onClose={() => setShowMultipleMedicalCarriersModal(false)}
          />
        )}
      </OEContext.Provider>
    );
  };

  const RenderStatusBanner = () => {
    const onRefreshClick = async () => {
      setLastRefreshTime(new Date());
      queryClient.invalidateQueries([QUERY_KEYS.GET_CLIENTS]);
    };

    const GetLastRefreshString = () => {
      if (lastRefreshTime) {
        return `(Last refreshed: ${lastRefreshTime.toLocaleTimeString()})`;
      } else {
        return "";
      }
    };

    if (client.oeStatus === "Submitted") {
      if (loggedInUser?.hasAdminAppPrivilege) {
        return (
          <div
            style={{
              backgroundColor: "#d4edda",
              padding: 10,
              marginBottom: 20,
              borderRadius: ".25rem",
            }}
          >
            <span style={{ color: "#155724" }}>
              Client submitted rate model for review. Please review and publish
              the rates to PrismHR
            </span>
          </div>
        );
      } else {
        return (
          <div
            style={{
              backgroundColor: "#d4edda",
              padding: 10,
              marginBottom: 20,
              borderRadius: ".25rem",
            }}
          >
            <span style={{ color: "#155724" }}>
              Successfully submitted rate model for review. Benefit Specialist
              will review and publish the Rates. Please reach out to Specialist
              to make any changes
            </span>
          </div>
        );
      }
    } else if (client.oeStatus === "PublishInProgress") {
      if (loggedInUser?.hasAdminAppPrivilege) {
        return (
          <div
            style={{
              backgroundColor: "#fff3cd",
              padding: 10,
              marginBottom: 20,
              borderRadius: ".25rem",
            }}
          >
            <span style={{ color: "#721c24" }}>
              Rate publishing is in progress. Please check status after few mins{" "}
              <span
                style={{
                  cursor: "pointer",
                  fontWeight: "bold",
                  paddingLeft: "20px",
                }}
                onClick={onRefreshClick}
              >
                Refresh
              </span>
              <span style={{ fontStyle: "italic", paddingLeft: "10px" }}>
                {GetLastRefreshString()}
              </span>
            </span>
          </div>
        );
      } else {
        return (
          <div
            style={{
              backgroundColor: "#d4edda",
              padding: 10,
              marginBottom: 20,
              borderRadius: ".25rem",
            }}
          >
            <span style={{ color: "#155724" }}>
              Successfully submitted rate model for review. Benefit Specialist
              will review and publish the Rates. Please reach out to Specialist
              to make any changes
            </span>
          </div>
        );
      }
    } else if (client.oeStatus === "PublishError") {
      if (loggedInUser?.hasAdminAppPrivilege) {
        return (
          <div
            style={{
              backgroundColor: "#f8d7da",
              padding: 10,
              marginBottom: 20,
              borderRadius: ".25rem",
            }}
          >
            <span style={{ color: "#721c24" }}>
              Error publishing rates.{" "}
              <span
                style={{ cursor: "pointer", fontWeight: "bold" }}
                onClick={() => setShowPublishErrorModel(true)}
              >
                View error details
              </span>
            </span>
          </div>
        );
      } else {
        return (
          <div
            style={{
              backgroundColor: "#d4edda",
              padding: 10,
              marginBottom: 20,
              borderRadius: ".25rem",
            }}
          >
            <span style={{ color: "#155724" }}>
              Successfully submitted rate model for review. Benefit Specialist
              will review and publish the Rates. Please reach out to Specialist
              to make any changes
            </span>
          </div>
        );
      }
    } else if (client.oeStatus === "Published") {
      return (
        <div
          style={{
            backgroundColor: "#d4edda",
            padding: 10,
            marginBottom: 20,
            borderRadius: ".25rem",
          }}
        >
          <span style={{ color: "#155724" }}>
            Successfully published rates.
          </span>
        </div>
      );
    } else {
      return null;
    }
  };

  const RenderParentClientInfo = () => {
    if (!isParentModelUiEnabled || !newModelDetailData?.isParentModel)
      return null;
    return (
      <div
        style={{
          margin: "10px 0",
          padding: "10px",
          backgroundColor: "#f0f4f8",
          borderRadius: "4px",
        }}
      >
        <p
          style={{
            margin: 0,
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          This model applies to one or more child clients.{" "}
          <Button
            themeColor={"info"}
            onClick={() => setShowSubClientsModal(true)}
          >
            See more
          </Button>
        </p>
      </div>
    );
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          backgroundColor: "#fff",
          marginBottom: 20,
          marginTop: 20,
        }}
      >
        <div>
          <RenderNav />
        </div>
      </div>
      <SuccessBanner
        handler={useMutateUpdateModelRatesHandler}
        message={"Rates draft saved successfully."}
      />
      <ErrorBanner
        handler={useMutateUpdateModelRatesHandler}
        onError={() => setIsSavingDraft(false)}
      />
      {RenderStatusBanner()}
      {RenderParentClientInfo()}
      {RenderTab()}
      {showSubClientsModal && (
        <ManageSubClientsDialog
          key={newModelDetailData?.subClients?.map((c) => c.clientId).join(",")}
          allSubClients={client?.subClients || []}
          savedSubClients={newModelDetailData?.subClients || []}
          oePeriodId={oePeriodId}
          clientId={clientId}
          versionId={versionId}
          onClose={() => setShowSubClientsModal(false)}
        />
      )}
      {showParentSubmitWarning && (
        <ParentSubmitWarningDialog
          subClients={newModelDetailData?.subClients || []}
          onClose={() => setShowParentSubmitWarning(false)}
          onConfirm={() => {
            setShowParentSubmitWarning(false);
            setShowSubmitConfirmModel(true);
          }}
        />
      )}
      {showAddPlanModel && (
        <ClientRateModelAddPlan
          clientId={clientId}
          oePeriodId={oePeriodId}
          versionId={versionId}
          groupId={currentGroupId}
          onClose={() => setShowAddPlanModel(false)}
        />
      )}
      {showPublishErrorModel && (
        <ClientRateModelErrorDialog
          clientId={clientId}
          oePeriodId={oePeriodId}
          versionId={versionId}
          toggleDialog={() => setShowPublishErrorModel(false)}
        />
      )}
      {showMaxContributionModel && (
        <ClientMaxContributionModel
          clientId={clientId}
          oePeriodId={oePeriodId}
          versionId={versionId}
          maxContribution={currentGroupMaxContrib}
          onClose={() => setShowMaxContributionModel(false)}
        />
      )}
      {showCopyContribModel && (
        <ClientCopyContributionDialog
          clientId={clientId}
          oePeriodId={oePeriodId}
          versionId={versionId}
          groupId={currentGroupId}
          onClose={() => setShowCopyContribModel(false)}
        />
      )}
      {showSubmitErrorModel && (
        <AlertDialog
          title={"Incomplete Model"}
          message={
            "Please enter rate contribution for all plans and classes before submitting."
          }
          onClose={() => setShowSubmitErrorModel(false)}
        />
      )}
      {showSubmitConfirmModel && (
        <DisclosureModal
          clientId={clientId}
          clientName={client?.clientName}
          oePeriodId={oePeriodId}
          versionId={versionId}
          oePeriod={oePeriod}
          onClose={() => setShowSubmitConfirmModel(false)}
          onAccept={() => handleDisclosureAccept(false)}
          loggedInUser={loggedInUser}
          isBuiltByBroker={isBuiltByBroker}
        />
      )}
      {showThankYou && <ThankYouModal onClose={handleThankYouClose} />}
      {showOverrideStatusDialog && (
        <OverrideOEStatusDialog
          clientId={clientId}
          oePeriodId={oePeriodId}
          onClose={() => setShowOverrideStatusDialog(false)}
          onSuccess={() => setShowOverrideStatusDialog(false)}
        />
      )}
    </div>
  );
};

const NewOfferingPlanDetailsMessage = ({ modelData }) => {
  const newOfferingOfferTypes = React.useMemo(() => {
    const offerTypes = Array.isArray(modelData?.newOfferingOfferTypes)
      ? [...modelData.newOfferingOfferTypes]
      : [];

    if (offerTypes.length === 0) {
      if (modelData?.hasDentalNewOffering) {
        offerTypes.push("DEN");
      }

      if (modelData?.hasVisionNewOffering) {
        offerTypes.push("VIS");
      }
    }

    return [...new Set(offerTypes)];
  }, [
    modelData?.hasDentalNewOffering,
    modelData?.hasVisionNewOffering,
    modelData?.newOfferingOfferTypes,
  ]);

  const offerTypeLabels = React.useMemo(() => {
    const labels = {
      MED: "medical",
      DEN: "dental",
      VIS: "vision",
    };

    return newOfferingOfferTypes.map(
      (offerTypeCode) => labels[offerTypeCode] ?? offerTypeCode,
    );
  }, [newOfferingOfferTypes]);

  const memoizedPlanIds = React.useMemo(() => {
    if (newOfferingOfferTypes.length === 0) {
      return [];
    }

    const plans = modelData.groups.flatMap((group) => {
      return group.rates
        .filter((e) => newOfferingOfferTypes.includes(e.planOfferType))
        .map((rate) => rate.planId);
    });

    return [...new Set(plans)];
  }, [modelData, newOfferingOfferTypes]);

  const memoizedPlanIdsString = React.useMemo(() => {
    if (!memoizedPlanIds || memoizedPlanIds.length === 0) {
      return "";
    }

    return memoizedPlanIds.join(", ");
  }, [memoizedPlanIds]);

  const addedPlansString = React.useMemo(() => {
    if (!modelData?.newlyAddedPlans) {
      return null;
    }

    const addedPlans = modelData.newlyAddedPlans.filter(
      (e) => !memoizedPlanIds.some((id) => id === e),
    );

    return addedPlans.join(", ");
  }, [modelData?.newlyAddedPlans, memoizedPlanIds]);

  const removedPlansString = React.useMemo(() => {
    // SOF-838: include carrier-terminated plans still on the client's rate model, not only
    // rows the user soft-deleted; both are excluded from publish so both must be shown.
    const carrierTerminated = (modelData?.groups ?? [])
      .flatMap((group) => group?.rates ?? [])
      .filter((rate) => rate?.isCarrierTerminated === true)
      .map((rate) => rate.planId)
      .filter(Boolean);

    const union = Array.from(
      new Set([...(modelData?.deletedPlans ?? []), ...carrierTerminated]),
    );

    return union.length > 0 ? union.join(", ") : null;
  }, [modelData?.deletedPlans, modelData?.groups]);

  const newOfferingDescription = React.useMemo(() => {
    if (offerTypeLabels.length === 0) {
      return null;
    }

    const formatter = new Intl.ListFormat("en", {
      style: "long",
      type: "conjunction",
    });

    return `This client has new ${formatter.format(offerTypeLabels)} offerings. Review the below plans before publishing. The publish flow will create any missing Prism benefit setup automatically.`;
  }, [offerTypeLabels]);

  return (
    <div style={{ paddingTop: 20, color: "red", fontSize: 18 }}>
      NOTE: <br />
      {newOfferingDescription && (
        <>
          {newOfferingDescription}
          <br />
          <span style={{ fontWeight: "bold" }}>{memoizedPlanIdsString}</span>
          <br />
        </>
      )}
      {addedPlansString && (
        <>
          <br />
          The following new plans are added to this client:
          <br />
          <span style={{ fontWeight: "bold" }}>{addedPlansString}</span>
          <br />
        </>
      )}
      {removedPlansString && (
        <>
          <br />
          The following plans are removed from this client:
          <br />
          <span style={{ fontWeight: "bold" }}>{removedPlansString}</span>
          <br />
        </>
      )}
    </div>
  );
};

export default ClientRateModelLayout;
